import React, { useState } from "react";
import { CHART_1, CHART_2, INK, MUTED, GRID, PAPER, alpha } from "./palette";

// ---- dot_plot: every score as a dot, stacked where scores tie, one row per group ----

// For whole-number ratings, where many people give the same score: each person is a green dot
// (the data) stacked at their score, and each group's median (a teal line drawn over the dots, "Mdn") and middle half
// (Q₁ to Q₃, a pale teal band) are the idea. No means. Props come from ChartData#dot_plot:
//   groups [{ name, values (sorted), n, median, q1, q3 }], range [lo, hi] (the axis),
//   tie { value, count, from, to, rank } (the most common score across everyone and the rank it shares),
//   plus axisLabel and noun from the page.
// The tokens and the Legend/Tooltip conventions match ConceptChart.jsx.
const W = 640;
const M = { top: 8, right: 16, left: 16 };
const LABEL_W = 84, R = 4.5, STEP = 2 * R + 1;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
// Up to two decimals, without trailing zeros: 3, 3.5, 3.25 (as the data box prints quartiles).
const trim = (x) => fmt(x, 2).replace(/\.?0+$/, "");
const lower = (s) => (/^.[A-Z0-9]/.test(s) ? s : s.toLowerCase());
const plural = (k, noun) => `${k} ${noun}${k === 1 ? "" : "s"}`;
const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

// kind: dot (default), line, square (a shaded band).
function Legend({ items }) {
	const swatch = (kind, color) => {
		if (kind === "line") return <span className="inline-block h-3 w-0.5 rounded" style={{ background: color }} />;
		if (kind === "square") return <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />;
		return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{items.map(({ label, color, kind }) => (
				<span key={label} className="flex items-center gap-1.5">
					{swatch(kind, color)}
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, y, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${y})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function DotPlotChart({ groups = [], range, tie, axisLabel = "", noun = "participant" }) {
	const [hover, setHover] = useState(null);
	if (!Array.isArray(groups) || groups.length < 2) return null;
	const all = groups.flatMap((g) => g.values);
	const [lo, hi] = range || [Math.min(...all), Math.max(...all)];
	const l = M.left + LABEL_W, r = W - M.right - 8;
	const x = (v) => l + ((v - lo) / (hi - lo || 1)) * (r - l);
	// Whole-number scales get a tick at every point; otherwise about six.
	const whole = all.every(Number.isInteger) && hi - lo <= 12;
	const step = whole ? 1 : [1, 2, 2.5, 5, 10].map((m) => m * 10 ** Math.floor(Math.log10((hi - lo) / 6 || 1))).find((s) => s >= (hi - lo) / 6);
	const ticks = [];
	for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) ticks.push(+t.toFixed(10));
	// The positions everyone fills when all the scores are lined up: the rank a tied score shares.
	const rankOf = (v) => { const below = all.filter((s) => s < v).length, k = all.filter((s) => s === v).length; return below + (k + 1) / 2; };
	const pooled = (v) => all.filter((s) => s === v).length;

	// Each row: a line for the Mdn and quartile labels, then the stacks, tallest at the top of the band.
	const rows = [];
	let top = M.top;
	groups.forEach((g) => {
		const stacks = Object.entries(g.values.reduce((acc, v) => ({ ...acc, [v]: (acc[v] || 0) + 1 }), {})).map(([v, k]) => [+v, k]).sort((a, b) => a[0] - b[0]);
		const tallest = Math.max(...stacks.map(([, k]) => k));
		const bandTop = top + 20, base = bandTop + tallest * STEP + 6;
		rows.push({ ...g, stacks, top, bandTop, base });
		top = base + 18;
	});
	const axisY = rows[rows.length - 1].base;
	const height = axisY + 38;
	const nouns = `${noun}s`;
	const middle = (g) => `${trim(g.q1)} to ${trim(g.q3)}`;
	const everywhere = tie && rows.every((g) => g.values.includes(tie.value)) ? `, from ${rows.length === 2 ? "both" : "all"} groups,` : "";
	const scoreName = lower(axisLabel.replace(/\s*\(.*\)\s*$/, "")) || "score";

	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Median (Mdn)", color: CHART_1, kind: "line" }, { label: "Middle half (Q₁ to Q₃)", color: alpha(CHART_1, 0.16), kind: "square" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${axisLabel || "Scores"}, one dot per ${noun}, stacked where scores tie. ${rows.map((g) => `${g.name}: ${g.n} ${nouns}, median ${trim(g.median)}, middle half ${middle(g)}.`).join(" ")}`}>
				{ticks.map((t) => <line key={`g${t}`} x1={x(t)} x2={x(t)} y1={M.top + 14} y2={axisY} stroke={GRID} strokeWidth="1" strokeDasharray="2 3" />)}
				{rows.map((g) => {
					const xm = x(g.median), q1 = x(g.q1), q3 = x(g.q3);
					// Quartile labels sit just outside the band, unless they'd run into the Mdn label.
					const roomLeft = xm - q1 > 44, roomRight = q3 - xm > 44;
					return (
						<g key={g.name}>
							<text x={M.left} y={(g.bandTop + g.base) / 2} fontSize="12" fill={INK} fontWeight="600" dominantBaseline="middle">{g.name}</text>
							<text x={M.left} y={(g.bandTop + g.base) / 2 + 15} fontSize="11" fill={MUTED} dominantBaseline="middle">n = {g.n}</text>
							<rect x={q1} y={g.bandTop} width={Math.max(2, q3 - q1)} height={g.base - g.bandTop} fill={CHART_1} opacity="0.16" rx="2" />
							<Halo x={xm} y={g.bandTop - 8} textAnchor="middle" fill={CHART_1} fontWeight="600">Mdn {trim(g.median)}</Halo>
							{roomLeft && <Halo x={q1} y={g.bandTop - 8} textAnchor="middle" fill={MUTED}>Q₁ {trim(g.q1)}</Halo>}
							{roomRight && <Halo x={q3} y={g.bandTop - 8} textAnchor="middle" fill={MUTED}>Q₃ {trim(g.q3)}</Halo>}
							<line x1={l - 8} x2={r + 8} y1={g.base} y2={g.base} stroke={MUTED} strokeWidth="1" />
							{g.stacks.map(([v, k]) => (
								<g key={v} onMouseEnter={() => setHover({ cx: x(v), y: g.top, lines: [`${g.name}, a score of ${trim(v)}`, `${k} of ${g.n} ${nouns}`, `Rank ${trim(rankOf(v))} among all ${all.length}, shared by all ${pooled(v)} with a ${trim(v)}`] })}
									onMouseLeave={() => setHover(null)}>
									<rect x={x(v) - R - 2} y={g.base - k * STEP - 2} width={2 * R + 4} height={k * STEP + 2} fill="transparent" />
									{Array.from({ length: k }, (_, i) => (
										<circle key={i} cx={x(v)} cy={g.base - R - 1 - i * STEP} r={R} fill={CHART_2} stroke={PAPER} strokeWidth="1.5" />
									))}
								</g>
							))}
							{/* The median on top of the dots, so it shows through a tall stack, and as a tick below the line. */}
							<line x1={xm} x2={xm} y1={g.bandTop - 4} y2={g.base + 6} stroke={PAPER} strokeWidth="5" pointerEvents="none" />
							<line x1={xm} x2={xm} y1={g.bandTop - 4} y2={g.base + 6} stroke={CHART_1} strokeWidth="2.5" pointerEvents="none" />
						</g>
					);
				})}
				{ticks.map((t) => <text key={t} x={x(t)} y={axisY + 18} fontSize="11" fill={MUTED} textAnchor="middle">{trim(t)}</text>)}
				{axisLabel && <text x={W - M.right} y={height - 2} fontSize="11" fill={MUTED} textAnchor="end">{axisLabel}</text>}
				{hover && <Tooltip x={hover.cx} y={hover.y} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each dot is one {noun}’s {scoreName}, stacked where {scoreName}s tie. The teal line marks each group’s median and the band its middle half, from the lower to the upper quartile (Q₁ to Q₃): {sentenceList(rows.map((g) => `${middle(g)} for ${lower(g.name)}`))}.
				{tie && tie.count > 1 ? ` Tied scores share a rank: the ${tie.count} ${nouns} with a ${trim(tie.value)}${everywhere} fill positions ${tie.from} to ${tie.to} of ${all.length}, so each gets rank ${trim(tie.rank)}.` : ""}
			</figcaption>
		</figure>
	);
}
